/**
 * L2 label cache — IndexedDB-backed, sync read surface.
 *
 * Sits behind the in-memory `_cache` / `_metaCache` in `dimensionDefs.js`.
 * The goal is to render dim labels (endpoint hostnames, cluster names,
 * eventually Linear ticket titles) instantly on page load instead of
 * flashing the raw id while a network call resolves them.
 *
 * Why IDB, not localStorage
 *   LS caps out around 5 MB per origin. A tenant with 50k endpoints
 *   alone would blow that ceiling — and writes would silently fail
 *   once quota is hit, leaving the user with no persisted labels and
 *   no warning. IDB gets hundreds of MB to GBs, fits the real data.
 *
 * Sync reads on top of async storage
 *   IDB is async, but render paths (`scopeStepLabel`, `useDimensionEntry`)
 *   need a synchronous answer or they flash the raw id. We keep an
 *   in-memory `Map` per (tenant, dim) as the read-of-record. The Map
 *   is hydrated lazily from IDB via an async cursor scan on first
 *   access to its slot; subsequent reads are sync.
 *
 *   Cold paint UX: until the slot's first IDB load finishes
 *   (~10-100ms depending on size), reads return null and the caller
 *   queues a network fetch as it would today. When the load completes
 *   we fire registered ready callbacks so consumers can re-render —
 *   `dimensionDefs.js` wires that into its existing `notify()` so
 *   sidebar components pick up the fresh labels automatically.
 *
 *   Pre-warm: callers (typically dimensionDefs on first label request)
 *   can invoke `preWarmLabelCache(tenantId)` to start IDB loads for
 *   every persistent dim before they're individually needed, hiding
 *   the per-dim warm-up under the latency budget of the first paint.
 *
 * Storage layout
 *   DB:          one `label-cache-v4:<tenantId>:<userId>` DB per owner
 *   Store:       "labels" keyPath="key" -> "<owner>:<dim>:<id>"
 *   Index:       "byScope" on "scope" field -> "<owner>:<dim>"
 *   Row shape:   { key, scope, tenantId, dim, id, label, meta, fetchedAt }
 *
 * Write batching
 *   Writes accumulate in a per-slot `dirty` Map. A 250ms debounce
 *   coalesces a burst of label resolutions into one transaction with
 *   N puts. Deletes (TTL eviction) ride the same batch as `null`
 *   markers.
 *
 * Failure modes
 *   - IDB unavailable (Safari private mode historically): module
 *     degrades to memory-only. Public API still works, nothing
 *     persists across reloads.
 *   - Transaction errors: logged + swallowed. Worst case the user
 *     sees a flash on the next reload instead of an instant label.
 *   - Identity switch: each tenant-and-user owner has a separate DB and
 *     composite key prefix, so another identity starts with fresh slots.
 */

import { currentBrowserStorageOwner } from "./browserStorageScope.js";

const DB_NAME = "label-cache";
const SCOPED_DB_PREFIX = "label-cache-v4";
// Bumped past 2 to recover users whose browser sits at a stale v2
// schema from a prior deploy. `requested version (1) is less than
// the existing version (2)` errors fell back to memory-only — every
// label became a fresh fetch on cold load, amplifying first-paint
// work. Upgrading triggers a clean onupgradeneeded path that
// reinitialises the store at v3 (the upgrade handler always
// (re)creates the object store + index when missing, so existing
// rows are preserved when v3 already exists and recreated for
// users coming from the stale v2).
const DB_VERSION = 3;
const STORE = "labels";
const INDEX_BY_SCOPE = "byScope";
const WRITE_DEBOUNCE_MS = 250;

const _dbPromises = new Map();
let _legacyDiscardStarted = false;

// "tenantId:dim" -> Slot
// Slot = {
//   entries: Map<id, { label, meta, fetchedAt }>,
//   loaded:  boolean,
//   loading: Promise|null,
//   dirty:   Map<id, entry|null>,   // null = delete
//   writeTimer: number|null,
// }
const _slots = new Map();

// Callbacks fired whenever a slot finishes loading or after a flush.
// dimensionDefs.js subscribes once and forwards to its own `notify()`
// so React components re-render when labels arrive from IDB.
const _readySubs = new Set();

// Tenants we've already kicked pre-warm for. Survives across calls so
// requestLabel can call preWarmLabelCache as often as it likes without
// piling on duplicate cursor scans. New tenant id → new entry.
const _preWarmedFor = new Set();

function slotKeyForOwner(owner, dim) {
  return `${owner.key}:${dim}`;
}

function rowKeyForOwner(owner, dim, id) {
  return `${owner.key}:${dim}:${id}`;
}

function getDbForOwner(owner) {
  if (_dbPromises.has(owner.key)) return _dbPromises.get(owner.key);
  if (!_legacyDiscardStarted) {
    _legacyDiscardStarted = true;
    try { indexedDB.deleteDatabase(DB_NAME); } catch { /* unavailable */ }
  }
  const dbPromise = new Promise((resolve, reject) => {
    let req;
    try {
      req = indexedDB.open(`${SCOPED_DB_PREFIX}:${owner.key}`, DB_VERSION);
    } catch (err) {
      reject(err);
      return;
    }
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "key" });
        store.createIndex(INDEX_BY_SCOPE, "scope", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("IDB upgrade blocked"));
  }).catch((err) => {
    console.warn("[labelCache] IDB unavailable, running memory-only:", err);
    return null; // sentinel — every IDB op checks for this
  });
  _dbPromises.set(owner.key, dbPromise);
  return dbPromise;
}

function notifyReady() {
  for (const cb of _readySubs) {
    try { cb(); } catch (err) { console.warn("[labelCache] subscriber failed", err); }
  }
}

function ensureSlot(tenantId, dim) {
  const owner = currentBrowserStorageOwner(tenantId);
  if (!owner) return null;
  const k = slotKeyForOwner(owner, dim);
  let slot = _slots.get(k);
  if (slot) return slot;
  slot = {
    owner,
    tenantId,
    dim,
    entries: new Map(),
    loaded: false,
    loading: null,
    dirty: new Map(),
    writeTimer: null,
  };
  _slots.set(k, slot);
  slot.loading = loadSlotFromIdb(slot);
  return slot;
}

async function loadSlotFromIdb(slot) {
  const { owner, tenantId, dim } = slot;
  const scope = slotKeyForOwner(owner, dim);
  try {
    const db = await getDbForOwner(owner);
    if (!db) {
      slot.loaded = true;
      slot.loading = null;
      notifyReady();
      return;
    }
    const tx = db.transaction(STORE, "readonly");
    const idx = tx.objectStore(STORE).index(INDEX_BY_SCOPE);
    const range = IDBKeyRange.only(scope);
    await new Promise((res, rej) => {
      const req = idx.openCursor(range);
      req.onsuccess = (e) => {
        const cur = e.target.result;
        if (!cur) { res(); return; }
        const v = cur.value;
        if (v && typeof v.id === "string" && typeof v.label === "string" && typeof v.fetchedAt === "number") {
          // Don't overwrite a value that's been written by an in-session
          // `labelCacheSet` while we were loading — the in-memory write
          // is newer than whatever was on disk.
          if (!slot.entries.has(v.id)) {
            slot.entries.set(v.id, {
              label: v.label,
              meta: v.meta ?? null,
              fetchedAt: v.fetchedAt,
            });
          }
        }
        cur.continue();
      };
      req.onerror = () => rej(req.error);
    });
  } catch (err) {
    console.warn(`[labelCache] load failed for ${tenantId}:${dim}`, err);
  }
  slot.loaded = true;
  slot.loading = null;
  notifyReady();
}

function scheduleFlush(slot) {
  if (!slot || slot.writeTimer) return;
  slot.writeTimer = setTimeout(() => flushSlot(slot), WRITE_DEBOUNCE_MS);
}

async function flushSlot(slot) {
  const { owner, tenantId, dim } = slot;
  slot.writeTimer = null;
  if (slot.dirty.size === 0) return;
  const scope = slotKeyForOwner(owner, dim);
  const batch = [...slot.dirty.entries()].map(([id, entry]) => ({
    id,
    entry,
    key: rowKeyForOwner(owner, dim, id),
  }));
  slot.dirty.clear();
  try {
    const db = await getDbForOwner(owner);
    if (!db) return; // memory-only mode
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    for (const { id, entry, key } of batch) {
      if (entry === null) {
        store.delete(key);
      } else {
        store.put({
          key, scope, tenantId, dim, id,
          label: entry.label,
          meta: entry.meta ?? null,
          fetchedAt: entry.fetchedAt,
        });
      }
    }
    await new Promise((res, rej) => {
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
      tx.onabort = () => rej(tx.error || new Error("aborted"));
    });
  } catch (err) {
    console.warn(`[labelCache] flush failed for ${tenantId}:${dim}`, err);
    // Don't restore the dirty entries — they'd just re-fail on the
    // next write. The in-memory copy still serves the session.
  }
}

// ── Public API ─────────────────────────────────────────────────

// Sync read. Returns null when:
//   - inputs are missing
//   - the slot isn't loaded yet (caller should re-check on `onLabelCacheReady`)
//   - the entry doesn't exist
//   - the entry is older than `hardTtlMs` (evicted as a side effect)
export function labelCacheGet(tenantId, dim, id, hardTtlMs) {
  if (!tenantId || !dim || id == null) return null;
  const slot = ensureSlot(tenantId, dim);
  if (!slot) return null;
  if (!slot.loaded) return null;
  const idStr = String(id);
  const entry = slot.entries.get(idStr);
  if (!entry) return null;
  if (hardTtlMs != null && Date.now() - entry.fetchedAt > hardTtlMs) {
    slot.entries.delete(idStr);
    slot.dirty.set(idStr, null);
    scheduleFlush(slot);
    return null;
  }
  return entry;
}

// Mark an entry as soft-stale: still rendered but caller should refresh.
export function labelCacheIsSoftStale(tenantId, dim, id, softTtlMs) {
  if (softTtlMs == null) return false;
  const entry = labelCacheGet(tenantId, dim, id);
  if (!entry) return false;
  return Date.now() - entry.fetchedAt > softTtlMs;
}

export function labelCacheSet(tenantId, dim, id, label, meta) {
  if (!tenantId || !dim || id == null) return;
  const labelStr = String(label ?? "");
  if (!labelStr) return;
  const slot = ensureSlot(tenantId, dim);
  if (!slot) return;
  const idStr = String(id);
  const entry = { label: labelStr, meta: meta ?? null, fetchedAt: Date.now() };
  slot.entries.set(idStr, entry);
  slot.dirty.set(idStr, entry);
  scheduleFlush(slot);
}

// Async: clears one (tenant, dim) slot or the whole tenant. Used when
// a connection is removed or when the data shape changes. Safe to call
// without awaiting — the in-memory drop happens synchronously, only the
// IDB delete is async.
export async function labelCacheClear(tenantId, dim) {
  if (!tenantId) return;
  const owner = currentBrowserStorageOwner(tenantId);
  if (!owner) return;
  if (dim) {
    _slots.delete(slotKeyForOwner(owner, dim));
    try {
      const db = await getDbForOwner(owner);
      if (!db) return;
      const tx = db.transaction(STORE, "readwrite");
      const idx = tx.objectStore(STORE).index(INDEX_BY_SCOPE);
      const range = IDBKeyRange.only(slotKeyForOwner(owner, dim));
      await new Promise((res, rej) => {
        const req = idx.openCursor(range);
        req.onsuccess = (e) => {
          const c = e.target.result;
          if (c) { c.delete(); c.continue(); } else res();
        };
        req.onerror = () => rej(req.error);
      });
    } catch (err) {
      console.warn(`[labelCache] clear failed for ${tenantId}:${dim}`, err);
    }
    return;
  }
  const prefix = `${owner.key}:`;
  for (const k of [..._slots.keys()]) {
    if (k.startsWith(prefix)) _slots.delete(k);
  }
  _preWarmedFor.delete(owner.key);
  try {
    const db = await getDbForOwner(owner);
    if (!db) return;
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    // Primary key prefix scan: `${tenantId}:` ..< `${tenantId};` (the
    // ASCII char after ':' is ';', giving us a tight upper bound).
    const range = IDBKeyRange.bound(`${owner.key}:`, `${owner.key};`, false, true);
    await new Promise((res, rej) => {
      const req = store.openCursor(range);
      req.onsuccess = (e) => {
        const c = e.target.result;
        if (c) { c.delete(); c.continue(); } else res();
      };
      req.onerror = () => rej(req.error);
    });
  } catch (err) {
    console.warn(`[labelCache] clear failed for tenant ${tenantId}`, err);
  }
}

// Idempotent — kicks off the IDB load for (tenant, dim) if not started.
// Callers can call freely; the slot tracks its own loading state.
export function labelCacheEnsure(tenantId, dim) {
  if (!tenantId || !dim) return;
  ensureSlot(tenantId, dim);
}

// Pre-warm every persistent dim for a tenant at once. Each call after
// the first for a given tenant is a no-op.
export function preWarmLabelCache(tenantId, persistentDimKeys) {
  if (!tenantId || !persistentDimKeys?.length) return;
  const owner = currentBrowserStorageOwner(tenantId);
  if (!owner || _preWarmedFor.has(owner.key)) return;
  _preWarmedFor.add(owner.key);
  for (const dim of persistentDimKeys) ensureSlot(tenantId, dim);
}

// Subscribe to slot-ready / post-flush notifications. Returns an
// unsubscribe fn. The callback fires after every IDB load (one per
// slot, per page lifetime) — consumers should treat it as a "labels
// may have arrived, re-render" signal rather than carrying any data.
export function onLabelCacheReady(cb) {
  _readySubs.add(cb);
  return () => _readySubs.delete(cb);
}

export async function _resetLabelCacheMemoryForTests() {
  for (const slot of _slots.values()) {
    if (slot.writeTimer) clearTimeout(slot.writeTimer);
  }
  _slots.clear();
  _preWarmedFor.clear();
  const databases = await Promise.all([..._dbPromises.values()]);
  for (const db of databases) {
    try { db?.close(); } catch { /* already closed */ }
  }
  _dbPromises.clear();
  _readySubs.clear();
}
